<template>
  <div class="menu-container">
    <menu-item
      v-if="!hasUnreadMessages"
      :option="unreadOption"
      variant="icon"
      @click="$emit('mark-as-unread')"
    />
    <template v-for="option in statusMenuConfig" v-if="!chat.group">
      <menu-item
        v-if="show(option.key)"
        :key="option.key"
        :option="option"
        variant="icon"
        @click="toggleStatus(option.key, null)"
      />
    </template>
    <menu-item-with-submenu v-if="!chat.group" :option="snoozeMenuConfig">
      <menu-item
        v-for="(option, i) in snoozeMenuConfig.options"
        :key="i"
        :option="option"
        @click="snoozeConversation(option.snoozedUntil)"
      />
    </menu-item-with-submenu>
    <menu-item-with-submenu :option="priorityConfig">
      <menu-item
        v-for="(option, i) in priorityConfig.options"
        :key="i"
        :option="option"
        @click="assignPriority(option.key)"
      />
    </menu-item-with-submenu>
    <menu-item-with-submenu
      :option="labelMenuConfig"
      :sub-menu-available="!!labels.length"
    >
      <template>
        <menu-item
          v-for="label in labels"
          :key="label.id"
          :option="generateMenuLabelConfig(label, 'label')"
          variant="label"
          @click="$emit('assign-label', label)"
        />
      </template>
    </menu-item-with-submenu>
    <menu-item-with-submenu
      :option="agentMenuConfig"
      :sub-menu-available="!!assignableAgents.length"
    >
      <agent-loading-placeholder v-if="assignableAgentsUiFlags.isFetching" />
      <template v-else>
        <menu-item
          v-for="agent in assignableAgents"
          :key="agent.id"
          :option="generateMenuLabelConfig(agent, 'agent')"
          variant="agent"
          @click="$emit('assign-agent', agent)"
        />
      </template>
    </menu-item-with-submenu>
    <menu-item-with-submenu
      :option="teamMenuConfig"
      :sub-menu-available="!!teams.length"
    >
      <menu-item
        v-for="team in teams"
        :key="team.id"
        :option="generateMenuLabelConfig(team, 'team')"
        @click="$emit('assign-team', team)"
      />
    </menu-item-with-submenu>
  </div>
</template>

<script>
import MenuItem from './menuItem.vue';
import MenuItemWithSubmenu from './menuItemWithSubmenu.vue';
import wootConstants from 'dashboard/constants/globals';
import snoozeTimesMixin from 'dashboard/mixins/conversation/snoozeTimesMixin';
import agentMixin from 'dashboard/mixins/agentMixin';
import { mapGetters } from 'vuex';
import AgentLoadingPlaceholder from './agentLoadingPlaceholder.vue';
import alertMixin from 'shared/mixins/alertMixin';
export default {
  components: {
    MenuItem,
    MenuItemWithSubmenu,
    AgentLoadingPlaceholder,
  },
  mixins: [snoozeTimesMixin, agentMixin, alertMixin],
  props: {
    status: {
      type: String,
      default: '',
    },
    hasUnreadMessages: {
      type: Boolean,
      default: false,
    },
    inboxId: {
      type: Number,
      default: null,
    },
    chat_id: {
      type: Number,
      default: null,
    },
    chat: {
      type: Object,
      default: () => {},
    },
    inbox: {
      type: Object,
      default: () => {},
    },
    priority: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      STATUS_TYPE: wootConstants.STATUS_TYPE,
      unreadOption: {
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
        icon: 'mail',
      },
      statusMenuConfig: [
        {
          key: wootConstants.STATUS_TYPE.RESOLVED,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.RESOLVED'),
          icon: 'checkmark',
        },
        {
          key: wootConstants.STATUS_TYPE.PENDING,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.PENDING'),
          icon: 'book-clock',
        },
        {
          key: wootConstants.STATUS_TYPE.OPEN,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.REOPEN'),
          icon: 'arrow-redo',
        },
      ],
      snoozeMenuConfig: {
        key: 'snooze',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.TITLE'),
        icon: 'snooze',
        options: [
          {
            label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.NEXT_REPLY'),
            key: 'next-reply',
            snoozedUntil: null,
          },
          {
            label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.TOMORROW'),
            key: 'tomorrow',
            snoozedUntil: 'tomorrow',
          },
          {
            label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.NEXT_WEEK'),
            key: 'next-week',
            snoozedUntil: 'nextWeek',
          },
          {
            label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.CUSTOM'),
            key: 'custom',
            snoozedUntil: 'custom',
          },
        ],
      },
      labelMenuConfig: {
        key: 'label',
        icon: 'tag',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_LABEL'),
      },
      agentMenuConfig: {
        key: 'agent',
        icon: 'person-add',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_AGENT'),
      },
      teamMenuConfig: {
        key: 'team',
        icon: 'people-team-add',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_TEAM'),
      },
    };
  },
  computed: {
    priorityConfig() {
      return {
        key: 'priority',
        label: this.$t('CONVERSATION.PRIORITY.TITLE'),
        icon: 'warning',
        options: [
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
            key: null,
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
            key: 'urgent',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
            key: 'high',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
            key: 'medium',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
            key: 'low',
          },
        ].filter(item => item.key !== this.priority),
      };
    },
    ...mapGetters({
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      assignableAgentsUiFlags: 'inboxAssignableAgents/getUIFlags',
      currentUserID: 'getCurrentUserID',
      currentUser: 'getCurrentUser',
    }),
    filteredAgentOnAvailability() {
      const agents = this.$store.getters[
        'inboxAssignableAgents/getAssignableAgents'
      ](this.inboxId);
      const agentsByUpdatedPresence = this.getAgentsByUpdatedPresence(agents);
      const filteredAgents = this.sortedAgentsByAvailability(
        agentsByUpdatedPresence
      );
      return filteredAgents;
    },
    assignableAgents() {
      const listAgents = [
        {
          confirmed: true,
          name: 'None',
          id: null,
          role: 'agent',
          account_id: 0,
          email: 'None',
        },
        ...this.filteredAgentOnAvailability,
      ];

      const agentFilters = listAgents.filter(
        agent => agent.id !== this.currentUser.id
      );
      const assign_to_me = this.currentUser.permissions.assign_to_me;
      return assign_to_me ? listAgents : agentFilters;
    },
  },
  mounted() {
    this.validateAssigned();
    this.$store.dispatch('inboxAssignableAgents/fetch', [this.inboxId]);
  },
  methods: {
    validateAssigned() {
      if (
        this.currentUserID !== this.chat?.meta?.assignee?.id &&
        !this.chat.meta.assignee
      ) {
        this.statusMenuConfig.push({
          key: 'assign_to_me',
          label: this.$t('CONVERSATION_SIDEBAR.SELF_ASSIGN'),
          icon: 'person-add',
        });
      }

      const isAssign = this.statusMenuConfig.find(
        obj => obj.key === 'assign_to_me'
      );
      if (
        this.currentUserID !== this.chat?.meta?.assignee?.id &&
        this.inbox.enable_fowarding_conversations &&
        this.currentUser.permissions.assign_to_me &&
        !isAssign
      ) {
        this.statusMenuConfig.push({
          key: 'assign_to_me',
          label: this.$t('CONVERSATION_SIDEBAR.SELF_ASSIGN'),
          icon: 'person-add',
        });
      }
    },
    toggleStatus(status, snoozedUntil) {
      if (status === 'assign_to_me') {
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.chat_id,
            agentId: this.currentUserID,
          })
          .then(() => {
            this.showAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
        return;
      }
      this.$emit('update-conversation', status, snoozedUntil);
    },
    snoozeConversation(snoozedUntil) {
      if (snoozedUntil != 'custom') {
        this.$emit(
        'update-conversation',
        this.STATUS_TYPE.SNOOZED,
        this.snoozeTimes[snoozedUntil] || null
      );
      } else {
        this.$emit('open-modal',true)
      }

    },
    assignPriority(priority) {
      this.$emit('assign-priority', priority);
    },
    show(key) {
      // If the conversation status is same as the action, then don't display the option
      // i.e.: Don't show an option to resolve if the conversation is already resolved.
      return this.status !== key;
    },
    generateMenuLabelConfig(option, type = 'text') {
      return {
        key: option.id,
        ...(type === 'icon' && { icon: option.icon }),
        ...(type === 'label' && { color: option.color }),
        ...(type === 'agent' && { thumbnail: option.thumbnail }),
        ...(type === 'agent' && { status: option.availability_status }),
        ...(type === 'text' && { label: option.label }),
        ...(type === 'label' && { label: option.title }),
        ...(type === 'agent' && { label: option.name }),
        ...(type === 'team' && { label: option.name }),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-container {
  padding: var(--space-smaller);
  background-color: var(--white);
  box-shadow: var(--shadow-context-menu);
  border-radius: var(--border-radius-normal);
}
</style>
